import { useState } from "react";
import useRealtime from "../../lib/useRealtime";
import { Wallet, CheckCircle2, Clock, Loader2, Receipt } from "lucide-react";

const TABS = [
  { value: "awaited_decision", label: "Awaited Decision" },
  { value: "processing", label: "Processing" },
  { value: "paid", label: "Paid" },
];

const STATUS_COLOR = {
  awaited_decision: { fg: "text-slate-500",  bg: "bg-slate-100",  Icon: Clock },
  processing:        { fg: "text-amber-700", bg: "bg-amber-50",   Icon: Loader2 },
  paid:              { fg: "text-[#1B5E20]", bg: "bg-emerald-50", Icon: CheckCircle2 },
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function ScEarnings() {
  const { data } = useRealtime("/sc/payouts-ledger");
  const [tab, setTab] = useState("processing");

  if (!data) return <div className="font-mono text-xs text-slate-400 mt-10 text-center">Loading…</div>;

  const totals = data.totals || {};
  const items = data[tab] || [];
  const s = STATUS_COLOR[tab];
  const Icon = s.Icon;

  return (
    <div data-testid="sc-earnings">
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">Earnings</div>
      <h1 className="text-2xl font-bold tracking-tight mt-1 text-slate-900">Your payouts.</h1>

      {/* Hero totals */}
      <div className="mt-5 bg-gradient-to-br from-[#1B5E20] to-[#134A18] p-5 rounded-2xl shadow-[0_4px_20px_rgba(27,94,32,0.25)]">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-200">Lifetime paid</div>
        <div className="font-mono text-4xl font-bold tracking-tight text-white mt-1" data-testid="paid-total">
          ₹{Number(totals.paid || 0).toLocaleString("en-IN")}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-200/80">Processing</div>
            <div className="text-xl font-bold text-amber-300 mt-0.5">
              ₹{Number(totals.processing || 0).toLocaleString("en-IN")}
            </div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-200/80">Awaited decision</div>
            <div className="text-xl font-bold text-white mt-0.5">
              ₹{Number(totals.awaited_decision || 0).toLocaleString("en-IN")}
            </div>
          </div>
        </div>
      </div>

      {/* Tab filter */}
      <div className="mt-5 flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1" data-testid="earnings-tabs">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            data-testid={`sc-earn-tab-${t.value}`}
            className={"flex-shrink-0 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest rounded-full border transition-all whitespace-nowrap " +
              (tab === t.value
                ? "bg-[#1B5E20] text-white border-[#1B5E20] shadow-sm"
                : "bg-white border-slate-200 text-slate-600 hover:text-slate-900")}
          >
            {t.label} ({(data[t.value] || []).length})
          </button>
        ))}
      </div>

      {/* List */}
      <div className="mt-4 space-y-2">
        {items.map((it) => (
          <div
            key={it.opportunity_id}
            className="bg-white border border-slate-200 p-3 rounded-2xl shadow-[0_2px_8px_rgba(15,23,42,0.04)]"
            data-testid={`payment-row-${it.opportunity_id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-bold text-slate-900 truncate">{it.customer_name}</div>
                <div className="font-mono text-[10px] text-slate-400 mt-0.5 truncate">{it.code} · {it.opp_name}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[#1B5E20] font-mono text-base font-bold">
                  ₹{Number(it.amount || 0).toLocaleString("en-IN")}
                </div>
                <div className="font-mono text-[9px] text-slate-400">your commission</div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${s.bg} ${s.fg}`}>
                <Icon className={"w-3 h-3 " + (tab === "processing" ? "animate-spin" : "")} />
                {tab === "awaited_decision" ? (it.quotation_status || "not sent") : tab}
              </span>
              {it.paid_at && (
                <span className="font-mono text-[10px] text-[#1B5E20]">Paid {fmtDate(it.paid_at)}</span>
              )}
            </div>
            {tab === "paid" && it.utr && (
              <div className="flex items-center gap-1.5 mt-2 text-[11px] text-slate-700 font-mono bg-slate-50 px-2 py-1 rounded" data-testid={`payment-utr-${it.opportunity_id}`}>
                <Receipt className="w-3 h-3 text-[#1B5E20]" /> UTR: {it.utr}
              </div>
            )}
            {it.notes && (
              <div className="text-[12px] text-slate-700 mt-2 whitespace-pre-wrap">{it.notes}</div>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-center py-10 bg-white border border-slate-200 rounded-2xl">
            <Wallet className="w-8 h-8 mx-auto text-slate-300" />
            <div className="font-mono text-xs text-slate-400 mt-3">Nothing in this bucket yet.</div>
          </div>
        )}
      </div>
    </div>
  );
}
